// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ApiErrorPayloadCommon } from "@imprivata-cloud/common";
import type { CodingContext } from "@imprivata-cloud/data-privacy-js";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { usePlatform } from "../../context/platform/hooks";
import type { HandoffTokenStatus } from "../../models/handoff-token-status";
import { apiClient } from "../client";

const getDeviceHandoffStatusQueryKey = "get-device-handoff-status";

interface GetDeviceHandoffStatusResponse {
  tokenStatus: HandoffTokenStatus;
  isSuccessfulVerification: boolean;
}

const getDeviceHandoffStatusQuery = async (
  token: string,
  codingContext: CodingContext | undefined
): Promise<GetDeviceHandoffStatusResponse> => {
  try {
    const response = await apiClient.get<GetDeviceHandoffStatusResponse>(
      `handoff/status?id=${token}`,
      codingContext
    );

    return response;
  } catch (error: unknown) {
    const err = error as ApiErrorPayloadCommon;
    throw {
      cause: err.error.code ?? "unknown",
      message: err.error.message ?? "Unknown error",
      code: err.error.code ?? "unknown",
      data: err.error.data,
    };
  }
};

export const useGetDeviceHandoffStatusQuery = (pollingIntervalMs = 2000) => {
  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState<string>("");
  const { codingContext } = usePlatform();
  const query = useQuery({
    queryKey: [getDeviceHandoffStatusQueryKey],
    queryFn: () => getDeviceHandoffStatusQuery(token, codingContext),
    enabled: enabled && !!token,
    retry: false,
    // TanStack Query's built-in polling with refetchInterval
    // Poll while enabled and stop when status is 'Completed'. A failed request does not stop
    // polling: the next interval retries, and callers stop it explicitly with disable().
    refetchInterval: (query) =>
      query.state.data?.tokenStatus === "Completed" ? false : pollingIntervalMs,
    // Ensure polling waits for previous request to complete
    refetchIntervalInBackground: false,
  });

  return {
    tokenStatus: query.data?.tokenStatus,
    isSuccessfulVerification: query.data?.isSuccessfulVerification,
    isLoading: query.isLoading,
    error: query.error,
    setToken: (token: string) => {
      setToken(token);
      setEnabled(true);
    },
    getToken: () => token,
    disable: () => setEnabled(false),
  };
};
